import type { RegisteredAccount } from "../../types/account";

// backend/prime/accounts/opening_state.py LIABILITY_TYPES: the account types owed, not held.
const LIABILITY_TYPES: readonly string[] = ["CREDITCARD", "CREDITLINE"];

/** True for a card or credit line: its starting balance is asked as what was owed. */
export function isOwedAccount(account: RegisteredAccount): boolean {
  return LIABILITY_TYPES.includes(account.account_type ?? "");
}

/**
 * The row's name: the nickname, else "Account ending <last6>", else, when the
 * file named no account (`last6` empty), "account not yet identified".
 */
export function accountName(account: RegisteredAccount): string {
  if (account.nickname) return account.nickname;
  if (account.last6) return `Account ending ${account.last6}`;
  return "account not yet identified";
}

/**
 * The ONE question card a row shows, in the order the server answers them:
 * the replacement question first (it refuses an opening while open), then the
 * starting balance, then the identify question, which is a card only when
 * `last6` is empty. The nickname is never a card (it is the row's inline edit).
 */
export function questionCard(
  account: RegisteredAccount,
): "replacement" | "opening" | "identify" | null {
  const pending = account.pending_questions;
  if (pending.includes("replacement") && account.replacement_candidate?.question) {
    return "replacement";
  }
  if (pending.includes("opening")) return "opening";
  if (pending.includes("nickname") && account.last6 === "") return "identify";
  return null;
}

/** What a row's name reads as: the nickname, the institution, the type, the number, the purpose. */
function nameKey(account: RegisteredAccount): string {
  return [
    account.nickname ?? "",
    account.institution_name ?? "",
    account.account_type ?? "",
    account.last6,
    account.purpose ?? "",
  ].join("\u0000");
}

/**
 * The ids of the rows whose name would read the same as another row's
 * (the coordinator's ruling on walk-through finding 2): only these show the
 * served `routing_last4`, as "routing ending 0015".
 */
export function rowsReadingAlike(accounts: readonly RegisteredAccount[]): ReadonlySet<number> {
  const counts = new Map<string, number>();
  for (const account of accounts) {
    counts.set(nameKey(account), (counts.get(nameKey(account)) ?? 0) + 1);
  }
  return new Set(
    accounts.filter((a) => (counts.get(nameKey(a)) ?? 0) > 1).map((account) => account.id),
  );
}
